<template>
	<div class="horse-menu-mobile d-md-none">
		<div class="menu-header">
			<h2 class="menu-title text-truncate mb-0">{{ horse.horse_nom }}</h2>
			<a href="#" class="menu-icon" @click.prevent="$emit('openPhotoMenu')">
				<font-awesome-icon :icon="['fal', 'camera']" />
			</a>
			<a href="#" class="menu-icon" @click.prevent="$emit('close')">
				<font-awesome-icon :icon="['fal', 'times']" />
			</a>
		</div>
		<div class="menu-content">
			<div class="menu-identity">
				<i>{{ horse.sexe_label }} / {{ formattedAge }} / {{ horse.robe_label }}</i>
			</div>
			<section v-if="statuses.length > 0" class="menu-section">
				<div v-for="(status, idx) in statuses" :key="idx">
					{{ status.season_label }} {{ status.horse_nom }}<br /><small>{{ status.status_label }}</small>
				</div>
			</section>
			<section v-if="hasLocalisation" class="menu-section">
				<div class="menu-section-header">
					<h3 class="mb-0">{{ $t('global.mouvements') }}</h3>
				</div>
				<div v-if="residenceLabel">{{ residenceLabel }}</div>
				<div v-if="intraLocationParentLabel">{{ intraLocationParentLabel }}</div>
				<div v-if="intraLocationLabel">{{ intraLocationLabel }}</div>
			</section>

			<section v-if="actes.length > 0" class="menu-section">
				<div class="menu-section-header">
					<h3 class="mb-0">{{ $t('global.actes_planifies') }}</h3>
				</div>
				<div class="list-group">
					<a
						v-for="acte in actes"
						:key="acte.actes_id"
						href="#"
						class="list-group-item list-group-item-action d-flex align-items-center justify-content-between"
						:class="{ 'text-danger': isLate(acte) }"
						@click.prevent="modifierActe(acte)"
					>
						<span class="text-truncate"><span class="acte-date">{{ formatActeDate(acte.actes_date) }}</span> - {{ acte.actetype_label }}</span>
						<font-awesome-icon :icon="['fal', 'angle-right']" />
					</a>
				</div>
			</section>



			<section class="menu-section">
				<hr class="menu-separator" />
				<MenuTabsMobile :swiper_tabs="swiper_tabs" :add_action="tabAddAction" @close="$emit('close')" />
			</section>
		</div>
	</div>
</template>

<script type="text/javascript">
	import Common from '@/assets/js/common.js'
	import ActesMixin from '@/mixins/Actes.js'
	import ContractMixin from '@/mixins/Contract.js'

	export default {
		name: 'HorseMenuMobile',
		mixins: [ActesMixin, ContractMixin],
		components: {
			MenuTabsMobile: () => import('@/components/MenuTabsMobile')
		},
		props: {
			horse: { type: Object, required: true },
			presence: { type: Object, default: null },
			horse_lieustationnement: { type: Object, default: null },
			horseIntraLocation: { type: Object, default: null },
			swiper_tabs: { type: Array, default: () => [] },
			horse_readonly: { type: Boolean, default: false }
		},
		data () {
			return {
				actes: []
			}
		},
		created() {
			this.loadActes()
		},
		methods: {
			async loadActes() {
				this.actes = await this.getPlannedHorseActs(this.horse.horse_id)
			},
			isLate(acte) {
				const today = new Date()
				today.setHours(0, 0, 0, 0)
				return acte.actes_date < today
			},
			/**
			 * Formate la date d'un acte en jour court, numéro du jour et mois abrégé (ex : « ven. 3 juil. »)
			 * @param  {Date} date Date de l'acte
			 * @return {String}
			 */
			formatActeDate(date) {
				if(!(date instanceof Date)) {
					date = Date.parseTz(date)
				}
				return date.toLocaleDateString(this.$i18n.locale(), { weekday: 'short', day: 'numeric', month: 'short' })
			},
			addMouvement() {
				this.$router.push({ name: 'WriteMouvement', params: { horse_id: this.horse.horse_id, from: this.$route.name } })
				this.$emit('close')
			},
			addActe() {
				this.$router.push({ name: 'acteAjout', params: { horse_id: this.horse.horse_id, from: this.$route.name } })
				this.$emit('close')
			},
			addEcho() {
				this.$router.push({ name: 'acteAjout', params: { horse_id: this.horse.horse_id, type_preselect: 'REPRO_ECHOGRAPHIE', from: this.$route.name } })
				this.$emit('close')
			},
			addNote() {
				this.$router.push({ name: 'notesManagement', params: { horse_id: this.horse.horse_id } })
				this.$emit('close')
			},
			addCroissance() {
				this.$router.push({ name: 'AddHorseCroissance', params: { horse_id: this.horse.horse_id } })
				this.$emit('close')
			},
			// Ouvre l'onglet propriétaires avec le formulaire d'ajout d'un tiers directement affiché
			addTiersHorse() {
				this.$router.push({ name: 'HorseTiers', params: { horse_id: this.horse.horse_id }, query: { add: '1' } })
				this.$emit('close')
			},
			async addContract() {
				this.$emit('close')
				const contract_id = await this.addContractOnlineFromHorse(this.horse.horse_id)
				if(!contract_id) return
				this.$router.push({ name: 'ContractFormv2', params: { contract_id: parseInt(contract_id), from: 'mareList' } })
			},
			/**
			 * Action d'ajout rapide associée à un onglet de la fiche cheval, null si l'onglet n'en propose pas
			 * ou si la fiche est en lecture seule
			 * @param  {Object} tab Onglet du swiper de la fiche cheval
			 * @return {Function|null}
			 */
			tabAddAction(tab) {
				if(this.horse_readonly) {
					return null
				}

				const actions = {
					HorseFichePlanning: this.addActe,
					HorseFicheActes: this.addActe,
					HorseFicheAllActes: this.addActe,
					HorseFicheMouvements: this.addMouvement,
					HorseGynecologie: this.addEcho,
					HorseTiers: this.addTiersHorse,
					ContractMareList: this.addContract,
					HorseSuiviCroissance: this.addCroissance,
					HorseFicheNotes: this.addNote
				}
				return actions[tab.href] || null
			},
			modifierActe(acte) {
				if(this.horse_readonly) return
				this.$router.push({ name: 'acteAjout', params: { horse_id: this.horse.horse_id, acte_id: acte.actes_id, state: 'modification', from: this.$route.name } })
			}
		},
		computed: {
			formattedAge() {
				return Common.formatHorseAge(this.horse)
			},
			hasLocalisation() {
				return Boolean(this.residenceLabel || this.intraLocationParentLabel || this.intraLocationLabel)
			},
			/**
			 * Libellé du lieu où se trouve le cheval : résidence du mouvement en cours si pertinent, sinon lieu de stationnement
			 * @return {String}
			 */
			residenceLabel() {
				if(this.presence && (this.presence.mouvement_type === 1 || this.presence.mouvement_type === 5)) {
					return this.presence.mouvement_label
				}
				return this.horse_lieustationnement ? this.horse_lieustationnement.tiers_rs : ''
			},
			intraLocationParentLabel() {
				if(this.horseIntraLocation && this.horseIntraLocation.mouvement_intra_location) {
					return this.horseIntraLocation.mouvement_intra_location.intralocation_parent_label
				}
				return ''
			},
			intraLocationLabel() {
				if(this.horseIntraLocation && this.horseIntraLocation.mouvement_intra_location) {
					return this.horseIntraLocation.mouvement_intra_location.intralocation_label
				}
				return ''
			},
			/**
			 * États physiologiques des saisons courante(s) et précédente (juments)
			 * @return {Array<{season_label: String, horse_nom: String, status_label: String}>}
			 */
			statuses() {
				const statuses = []

				if(this.horse.actual_status && this.horse.actual_season) {
					this.horse.actual_status.forEach((status, idx) => {
						if(status && this.horse.actual_season[idx]) {
							statuses.push({
								season_label: this.horse.actual_season[idx].season_label,
								horse_nom: status.horse_nom,
								status_label: Common.translateSeasonMareStatus(status.status)
							})
						}
					})
				}

				if(this.horse.previous_status && this.horse.previous_season) {
					statuses.push({
						season_label: this.horse.previous_season.season_label,
						horse_nom: this.horse.previous_status.horse_nom,
						status_label: Common.translateSeasonMareStatus(this.horse.previous_status.status)
					})
				}

				return statuses
			}
		}
	}
</script>
